export const trigonometryLessons = [
  {
    id: 'precalc-6',
    title: 'Unit Circle & Trigonometric Functions',
    unit: 'Trigonometry',
    description: 'Master the unit circle, evaluate trig functions at special angles, and understand periodicity',
    standards: ['CCSS.MATH.CONTENT.HSF.TF.A.2', 'NCTM: Geometry', 'Ohio: PC.T.1', 'NY: AI-F.TF.2'],
    vocabulary: [
      { term: 'Unit Circle', definition: 'A circle with radius 1 centered at the origin' },
      { term: 'Radian', definition: 'An angle measure where 2π radians = 360°' },
      { term: 'Reference Angle', definition: 'The acute angle formed with the x-axis' },
      { term: 'Sine', definition: 'sin(θ) = y-coordinate on the unit circle' },
      { term: 'Cosine', definition: 'cos(θ) = x-coordinate on the unit circle' },
      { term: 'Tangent', definition: 'tan(θ) = sin(θ)/cos(θ) = y/x' },
      { term: 'Period', definition: 'The interval after which a function repeats' },
      { term: 'Amplitude', definition: 'Half the distance between maximum and minimum values' }
    ],
    concepts: [
      'The unit circle has equation x² + y² = 1',
      'For angle θ, the point (cos θ, sin θ) lies on the unit circle',
      'Special angles: 0°, 30°, 45°, 60°, 90° (0, π/6, π/4, π/3, π/2 radians)',
      'sin²θ + cos²θ = 1 (Pythagorean identity)',
      'Period of sin and cos is 2π; period of tan is π',
      'ASTC rule: All Students Take Calculus (signs in quadrants I, II, III, IV)'
    ],
    examples: [
      {
        problem: 'Find sin(5π/6) using the unit circle',
        steps: [
          '5π/6 is in Quadrant II (between π/2 and π)',
          'Reference angle: π - 5π/6 = π/6',
          'In Quadrant II, sine is positive',
          'sin(π/6) = 1/2',
          'Therefore: sin(5π/6) = 1/2'
        ]
      },
      {
        problem: 'Convert 135° to radians',
        steps: [
          'Use conversion: radians = degrees × (π/180)',
          '135° × (π/180)',
          'Simplify: 135π/180 = 3π/4',
          'Answer: 3π/4 radians'
        ]
      }
    ],
    teacherTip: 'Have students memorize the unit circle values for special angles. Use hand tricks and patterns (like the √n/2 pattern for sine) to help retention.',
    questions: [
      {
        id: 'trig-1',
        question: 'What is sin(π/6)?',
        options: ['1/2', '√2/2', '√3/2', '1'],
        correctAnswer: 0,
        explanation: 'sin(30°) = sin(π/6) = 1/2.'
      },
      {
        id: 'trig-2',
        question: 'What is cos(π/4)?',
        options: ['1/2', '√2/2', '√3/2', '1'],
        correctAnswer: 1,
        explanation: 'cos(45°) = cos(π/4) = √2/2.'
      },
      {
        id: 'trig-3',
        question: 'What is tan(π/4)?',
        options: ['0', '1/2', '1', '√3'],
        correctAnswer: 2,
        explanation: 'tan(45°) = tan(π/4) = 1.'
      },
      {
        id: 'trig-4',
        question: 'Convert 90° to radians',
        options: ['π/4', 'π/3', 'π/2', 'π'],
        correctAnswer: 2,
        explanation: '90° × (π/180) = π/2 radians.'
      },
      {
        id: 'trig-5',
        question: 'Convert π/3 radians to degrees',
        options: ['30°', '45°', '60°', '90°'],
        correctAnswer: 2,
        explanation: '(π/3) × (180/π) = 60°.'
      },
      {
        id: 'trig-6',
        question: 'What is sin(π/2)?',
        options: ['0', '1/2', '√3/2', '1'],
        correctAnswer: 3,
        explanation: 'sin(90°) = sin(π/2) = 1.'
      },
      {
        id: 'trig-7',
        question: 'What is cos(0)?',
        options: ['0', '1/2', '√2/2', '1'],
        correctAnswer: 3,
        explanation: 'cos(0°) = cos(0) = 1.'
      },
      {
        id: 'trig-8',
        question: 'In which quadrant is sin positive and cos negative?',
        options: ['I', 'II', 'III', 'IV'],
        correctAnswer: 1,
        explanation: 'Quadrant II: x &lt; 0 (cos negative), y &gt; 0 (sin positive).'
      },
      {
        id: 'trig-9',
        question: 'What is the period of sin(x)?',
        options: ['π', '2π', 'π/2', '4π'],
        correctAnswer: 1,
        explanation: 'sin(x) repeats every 2π radians (360°).'
      },
      {
        id: 'trig-10',
        question: 'What is the period of tan(x)?',
        options: ['π/2', 'π', '2π', '3π'],
        correctAnswer: 1,
        explanation: 'tan(x) repeats every π radians (180°).'
      },
      {
        id: 'trig-11',
        question: 'What is sin²θ + cos²θ equal to?',
        options: ['0', '1', 'tan²θ', 'sec²θ'],
        correctAnswer: 1,
        explanation: 'Pythagorean identity: sin²θ + cos²θ = 1 for all θ.'
      },
      {
        id: 'trig-12',
        question: 'What is sin(π)?',
        options: ['-1', '0', '1', 'undefined'],
        correctAnswer: 1,
        explanation: 'sin(180°) = sin(π) = 0.'
      },
      {
        id: 'trig-13',
        question: 'What is cos(π)?',
        options: ['-1', '0', '1', 'undefined'],
        correctAnswer: 0,
        explanation: 'cos(180°) = cos(π) = -1.'
      },
      {
        id: 'trig-14',
        question: 'What is tan(π/6)?',
        options: ['1/2', '√3/3', '√3', '1'],
        correctAnswer: 1,
        explanation: 'tan(30°) = tan(π/6) = 1/√3 = √3/3.'
      },
      {
        id: 'trig-15',
        question: 'What is sin(7π/6)?',
        options: ['-1/2', '1/2', '-√3/2', '√3/2'],
        correctAnswer: 0,
        explanation: '7π/6 is in Quadrant III (reference angle π/6), sine is negative: -1/2.'
      },
      {
        id: 'trig-16',
        question: 'What is the amplitude of f(x) = 3sin(x)?',
        options: ['1', '2', '3', '6'],
        correctAnswer: 2,
        explanation: 'Amplitude is the coefficient of sin: |3| = 3.'
      },
      {
        id: 'trig-17',
        question: 'What is the range of sin(x)?',
        options: ['[0, 1]', '[-1, 1]', '[0, ∞)', 'All real numbers'],
        correctAnswer: 1,
        explanation: 'sin(x) oscillates between -1 and 1.'
      },
      {
        id: 'trig-18',
        question: 'What is cos(3π/2)?',
        options: ['-1', '0', '1', 'undefined'],
        correctAnswer: 1,
        explanation: 'cos(270°) = cos(3π/2) = 0.'
      },
      {
        id: 'trig-19',
        question: 'What is sin(5π/4)?',
        options: ['-√2/2', '√2/2', '-1/2', '1/2'],
        correctAnswer: 0,
        explanation: '5π/4 is in Quadrant III (reference angle π/4), sine is negative: -√2/2.'
      },
      {
        id: 'trig-20',
        question: 'What is tan(π/2)?',
        options: ['0', '1', '-1', 'undefined'],
        correctAnswer: 3,
        explanation: 'tan(π/2) = sin(π/2)/cos(π/2) = 1/0, which is undefined.'
      },
      {
        id: 'trig-21',
        question: 'Convert 2π/3 radians to degrees',
        options: ['60°', '90°', '120°', '150°'],
        correctAnswer: 2,
        explanation: '(2π/3) × (180/π) = 120°.'
      },
      {
        id: 'trig-22',
        question: 'What is cos(π/3)?',
        options: ['1/2', '√2/2', '√3/2', '1'],
        correctAnswer: 0,
        explanation: 'cos(60°) = cos(π/3) = 1/2.'
      },
      {
        id: 'trig-23',
        question: 'What is sin(π/3)?',
        options: ['1/2', '√2/2', '√3/2', '1'],
        correctAnswer: 2,
        explanation: 'sin(60°) = sin(π/3) = √3/2.'
      },
      {
        id: 'trig-24',
        question: 'In which quadrant is both sin and cos negative?',
        options: ['I', 'II', 'III', 'IV'],
        correctAnswer: 2,
        explanation: 'Quadrant III: both x and y coordinates are negative.'
      },
      {
        id: 'trig-25',
        question: 'What is the reference angle for 5π/3?',
        options: ['π/6', 'π/4', 'π/3', 'π/2'],
        correctAnswer: 2,
        explanation: '5π/3 is in Quadrant IV. Reference angle: 2π - 5π/3 = π/3.'
      },
      {
        id: 'trig-26',
        question: 'What is cos(5π/6)?',
        options: ['-√3/2', '√3/2', '-1/2', '1/2'],
        correctAnswer: 0,
        explanation: '5π/6 is in Quadrant II (reference angle π/6), cosine is negative: -√3/2.'
      },
      {
        id: 'trig-27',
        question: 'What is tan(π/3)?',
        options: ['1/√3', '1', '√3', '2'],
        correctAnswer: 2,
        explanation: 'tan(60°) = tan(π/3) = √3.'
      },
      {
        id: 'trig-28',
        question: 'What is sin(-π/6)?',
        options: ['-1/2', '1/2', '-√3/2', '√3/2'],
        correctAnswer: 0,
        explanation: 'sin is an odd function: sin(-θ) = -sin(θ), so sin(-π/6) = -1/2.'
      },
      {
        id: 'trig-29',
        question: 'What is cos(-π/4)?',
        options: ['-√2/2', '√2/2', '-1/2', '1/2'],
        correctAnswer: 1,
        explanation: 'cos is an even function: cos(-θ) = cos(θ), so cos(-π/4) = √2/2.'
      },
      {
        id: 'trig-30',
        question: 'What is the domain of tan(x)?',
        options: ['All real numbers', 'x ≠ nπ', 'x ≠ π/2 + nπ', 'x ≠ 2nπ'],
        correctAnswer: 2,
        explanation: 'tan(x) is undefined where cos(x) = 0, which occurs at x = π/2 + nπ.'
      }
    ]
  },
  {
    id: 'precalc-7',
    title: 'Trigonometric Identities',
    unit: 'Trigonometry',
    description: 'Prove and apply sum/difference, double-angle, and half-angle identities',
    standards: ['CCSS.MATH.CONTENT.HSF.TF.C.9', 'NCTM: Algebra', 'Ohio: PC.T.2', 'NY: AI-F.TF.9'],
    vocabulary: [
      { term: 'Identity', definition: 'An equation that is true for all values in the domain' },
      { term: 'Pythagorean Identity', definition: 'sin²θ + cos²θ = 1' },
      { term: 'Sum Formula', definition: 'sin(A + B) = sinA cosB + cosA sinB' },
      { term: 'Difference Formula', definition: 'cos(A - B) = cosA cosB + sinA sinB' },
      { term: 'Double-Angle Formula', definition: 'sin(2θ) = 2sinθ cosθ' },
      { term: 'Half-Angle Formula', definition: 'sin(θ/2) = ±√[(1 - cosθ)/2]' },
      { term: 'Reciprocal Identity', definition: 'csc θ = 1/sin θ, sec θ = 1/cos θ, cot θ = 1/tan θ' },
      { term: 'Quotient Identity', definition: 'tan θ = sin θ/cos θ' }
    ],
    concepts: [
      'Pythagorean identities: sin²θ + cos²θ = 1, 1 + tan²θ = sec²θ, 1 + cot²θ = csc²θ',
      'Sum formulas: sin(A±B) = sinA cosB ± cosA sinB, cos(A±B) = cosA cosB ∓ sinA sinB',
      'Double-angle: sin(2θ) = 2sinθ cosθ, cos(2θ) = cos²θ - sin²θ = 2cos²θ - 1 = 1 - 2sin²θ',
      'Half-angle formulas derived from double-angle formulas',
      'Use identities to simplify expressions and prove other identities',
      'Verify identities by transforming one side to match the other'
    ],
    examples: [
      {
        problem: 'Prove: (sin θ + cos θ)² = 1 + 2sin θ cos θ',
        steps: [
          'Expand left side: sin²θ + 2sin θ cos θ + cos²θ',
          'Group: (sin²θ + cos²θ) + 2sin θ cos θ',
          'Apply Pythagorean identity: 1 + 2sin θ cos θ',
          'This matches the right side ✓'
        ]
      },
      {
        problem: 'Find sin(75°) using sum formula',
        steps: [
          'Write 75° = 45° + 30°',
          'Apply: sin(45° + 30°) = sin(45°)cos(30°) + cos(45°)sin(30°)',
          'Substitute: (√2/2)(√3/2) + (√2/2)(1/2)',
          'Simplify: √6/4 + √2/4 = (√6 + √2)/4'
        ]
      }
    ],
    teacherTip: 'When proving identities, work on one side only and transform it to match the other. Start with the more complex side. Encourage students to memorize the fundamental identities.',
    questions: [
      {
        id: 'ident-1',
        question: 'What is 1 + tan²θ equal to?',
        options: ['sin²θ', 'cos²θ', 'sec²θ', 'csc²θ'],
        correctAnswer: 2,
        explanation: 'Pythagorean identity: 1 + tan²θ = sec²θ.'
      },
      {
        id: 'ident-2',
        question: 'What is sin(2θ) equal to?',
        options: ['2sin θ', '2cos θ', '2sin θ cos θ', 'sin²θ + cos²θ'],
        correctAnswer: 2,
        explanation: 'Double-angle formula: sin(2θ) = 2sin θ cos θ.'
      },
      {
        id: 'ident-3',
        question: 'What is cos(2θ) in terms of cos θ only?',
        options: ['2cos²θ', '2cos²θ - 1', 'cos²θ - 1', '1 - cos²θ'],
        correctAnswer: 1,
        explanation: 'Double-angle formula: cos(2θ) = 2cos²θ - 1.'
      },
      {
        id: 'ident-4',
        question: 'What is sin(A + B)?',
        options: ['sin A + sin B', 'sin A cos B + cos A sin B', 'sin A cos B - cos A sin B', 'cos A cos B + sin A sin B'],
        correctAnswer: 1,
        explanation: 'Sum formula: sin(A + B) = sin A cos B + cos A sin B.'
      },
      {
        id: 'ident-5',
        question: 'What is cos(A - B)?',
        options: ['cos A - cos B', 'cos A cos B - sin A sin B', 'cos A cos B + sin A sin B', 'sin A sin B - cos A cos B'],
        correctAnswer: 2,
        explanation: 'Difference formula: cos(A - B) = cos A cos B + sin A sin B.'
      },
      {
        id: 'ident-6',
        question: 'What is tan θ in terms of sin and cos?',
        options: ['sin θ + cos θ', 'sin θ - cos θ', 'sin θ / cos θ', 'cos θ / sin θ'],
        correctAnswer: 2,
        explanation: 'Quotient identity: tan θ = sin θ / cos θ.'
      },
      {
        id: 'ident-7',
        question: 'What is sec θ equal to?',
        options: ['1/sin θ', '1/cos θ', '1/tan θ', 'cos θ/sin θ'],
        correctAnswer: 1,
        explanation: 'Reciprocal identity: sec θ = 1/cos θ.'
      },
      {
        id: 'ident-8',
        question: 'What is csc θ equal to?',
        options: ['1/sin θ', '1/cos θ', '1/tan θ', 'sin θ/cos θ'],
        correctAnswer: 0,
        explanation: 'Reciprocal identity: csc θ = 1/sin θ.'
      },
      {
        id: 'ident-9',
        question: 'Simplify: sin²θ + cos²θ + tan²θ',
        options: ['1', '1 + tan²θ', 'sec²θ', 'Both B and C'],
        correctAnswer: 3,
        explanation: 'sin²θ + cos²θ = 1, so 1 + tan²θ = sec²θ. Both are correct.'
      },
      {
        id: 'ident-10',
        question: 'What is cos(2θ) in terms of sin θ only?',
        options: ['1 - 2sin²θ', '2sin²θ - 1', '1 - sin²θ', '2sin θ'],
        correctAnswer: 0,
        explanation: 'Double-angle formula: cos(2θ) = 1 - 2sin²θ.'
      },
      {
        id: 'ident-11',
        question: 'If sin θ = 3/5 and θ is in Quadrant I, what is cos θ?',
        options: ['3/5', '4/5', '5/3', '5/4'],
        correctAnswer: 1,
        explanation: 'sin²θ + cos²θ = 1, so (3/5)² + cos²θ = 1, cos²θ = 16/25, cos θ = 4/5 (positive in QI).'
      },
      {
        id: 'ident-12',
        question: 'What is tan(A + B)?',
        options: ['tan A + tan B', '(tan A + tan B)/(1 - tan A tan B)', '(tan A - tan B)/(1 + tan A tan B)', '(tan A + tan B)/(1 + tan A tan B)'],
        correctAnswer: 1,
        explanation: 'Sum formula: tan(A + B) = (tan A + tan B)/(1 - tan A tan B).'
      },
      {
        id: 'ident-13',
        question: 'Simplify: (sin θ)(csc θ)',
        options: ['0', '1', 'tan θ', 'sec θ'],
        correctAnswer: 1,
        explanation: 'sin θ · (1/sin θ) = 1.'
      },
      {
        id: 'ident-14',
        question: 'Simplify: (cos θ)(sec θ)',
        options: ['0', '1', 'tan θ', 'cot θ'],
        correctAnswer: 1,
        explanation: 'cos θ · (1/cos θ) = 1.'
      },
      {
        id: 'ident-15',
        question: 'What is 1 + cot²θ equal to?',
        options: ['tan²θ', 'sec²θ', 'csc²θ', 'cos²θ'],
        correctAnswer: 2,
        explanation: 'Pythagorean identity: 1 + cot²θ = csc²θ.'
      },
      {
        id: 'ident-16',
        question: 'Simplify: sin(-θ)',
        options: ['sin θ', '-sin θ', 'cos θ', '-cos θ'],
        correctAnswer: 1,
        explanation: 'sin is an odd function: sin(-θ) = -sin θ.'
      },
      {
        id: 'ident-17',
        question: 'Simplify: cos(-θ)',
        options: ['cos θ', '-cos θ', 'sin θ', '-sin θ'],
        correctAnswer: 0,
        explanation: 'cos is an even function: cos(-θ) = cos θ.'
      },
      {
        id: 'ident-18',
        question: 'What is sin(π/2 - θ)?',
        options: ['sin θ', 'cos θ', '-sin θ', '-cos θ'],
        correctAnswer: 1,
        explanation: 'Cofunction identity: sin(π/2 - θ) = cos θ.'
      },
      {
        id: 'ident-19',
        question: 'What is cos(π/2 - θ)?',
        options: ['sin θ', 'cos θ', '-sin θ', '-cos θ'],
        correctAnswer: 0,
        explanation: 'Cofunction identity: cos(π/2 - θ) = sin θ.'
      },
      {
        id: 'ident-20',
        question: 'Simplify: tan θ · cot θ',
        options: ['0', '1', 'sec²θ', 'csc²θ'],
        correctAnswer: 1,
        explanation: 'tan θ · (1/tan θ) = 1.'
      },
      {
        id: 'ident-21',
        question: 'If cos θ = 5/13 and θ is in Quadrant IV, what is sin θ?',
        options: ['12/13', '-12/13', '5/13', '-5/13'],
        correctAnswer: 1,
        explanation: 'sin²θ + (5/13)² = 1, sin²θ = 144/169, sin θ = -12/13 (negative in QIV).'
      },
      {
        id: 'ident-22',
        question: 'What is sin(A - B)?',
        options: ['sin A - sin B', 'sin A cos B - cos A sin B', 'sin A cos B + cos A sin B', 'cos A cos B - sin A sin B'],
        correctAnswer: 1,
        explanation: 'Difference formula: sin(A - B) = sin A cos B - cos A sin B.'
      },
      {
        id: 'ident-23',
        question: 'Simplify: sec²θ - tan²θ',
        options: ['0', '1', 'csc²θ', 'cot²θ'],
        correctAnswer: 1,
        explanation: 'From 1 + tan²θ = sec²θ, we get sec²θ - tan²θ = 1.'
      },
      {
        id: 'ident-24',
        question: 'What is cos(A + B)?',
        options: ['cos A + cos B', 'cos A cos B - sin A sin B', 'cos A cos B + sin A sin B', 'sin A sin B - cos A cos B'],
        correctAnswer: 1,
        explanation: 'Sum formula: cos(A + B) = cos A cos B - sin A sin B.'
      },
      {
        id: 'ident-25',
        question: 'Simplify: (1 - cos²θ)/sin θ',
        options: ['sin θ', 'cos θ', 'tan θ', 'cot θ'],
        correctAnswer: 0,
        explanation: '1 - cos²θ = sin²θ, so sin²θ/sin θ = sin θ.'
      },
      {
        id: 'ident-26',
        question: 'What is tan(2θ)?',
        options: ['2tan θ', '(2tan θ)/(1 - tan²θ)', '(2tan θ)/(1 + tan²θ)', 'tan²θ'],
        correctAnswer: 1,
        explanation: 'Double-angle formula: tan(2θ) = (2tan θ)/(1 - tan²θ).'
      },
      {
        id: 'ident-27',
        question: 'Simplify: sin θ/cos θ + cos θ/sin θ',
        options: ['1', '2', '1/(sin θ cos θ)', '(sin²θ + cos²θ)/(sin θ cos θ)'],
        correctAnswer: 2,
        explanation: '(sin²θ + cos²θ)/(sin θ cos θ) = 1/(sin θ cos θ).'
      },
      {
        id: 'ident-28',
        question: 'What is cot θ in terms of sin and cos?',
        options: ['sin θ/cos θ', 'cos θ/sin θ', '1/sin θ', '1/cos θ'],
        correctAnswer: 1,
        explanation: 'cot θ = 1/tan θ = cos θ/sin θ.'
      },
      {
        id: 'ident-29',
        question: 'Simplify: (tan θ + cot θ)(sin θ cos θ)',
        options: ['0', '1', 'sin θ + cos θ', 'tan θ + cot θ'],
        correctAnswer: 1,
        explanation: '(sin θ/cos θ + cos θ/sin θ)(sin θ cos θ) = sin²θ + cos²θ = 1.'
      },
      {
        id: 'ident-30',
        question: 'If tan θ = 4/3 and θ is in Quadrant III, what is sec θ?',
        options: ['5/3', '-5/3', '3/5', '-3/5'],
        correctAnswer: 1,
        explanation: '1 + tan²θ = sec²θ, so 1 + 16/9 = 25/9, sec θ = -5/3 (negative in QIII).'
      }
    ]
  }
];